import { ChevronDown } from "@icons";
import Tooltip from "@src/components/tooltip";
import classNames from "classnames";
import { ReactNode, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";

interface MenuItemBaseProp {
  label: string;
  icon: ReactNode;
  minimized?: boolean;
  setMobileMenuOpen: (open: boolean) => void;
}

interface MenuItemProps extends MenuItemBaseProp {
  to?: string;
  onClick?: null | (() => void);
}

interface MenuItemWithSubmenuProps extends MenuItemBaseProp {
  submenu: {
    key: string;
    component: React.FC<{
      minimized: boolean;
      setMobileMenuOpen: (open: boolean) => void;
    }>;
  }[];
}

export function MenuItem(props: MenuItemProps | MenuItemWithSubmenuProps) {
  const {
    label,
    icon,
    minimized = false,
    setMobileMenuOpen = () => {},
  } = props;
  const { submenu } = props as MenuItemWithSubmenuProps;
  const { to, onClick } = props as MenuItemProps;

  const location = useLocation();
  const isOnPage =
    (to && location.pathname.startsWith(to)) ||
    (submenu?.length > 0 &&
      submenu.some((item) => item.key === location.pathname));

  const [isOpen, setIsOpen] = useState(false);

  const content = (
    <div
      className={classNames(
        "w-full h-10 py-1 px-2 flex my-1 mx-auto",
        "group-focus/menuitem:bg-white group-focus/menuitem:text-secondary-700",
        "group-hover/menuitem:bg-white group-hover/menuitem:text-secondary-700",
        "rounded flex items-center",
        { "bg-white text-secondary-700": isOnPage }, // Apply styles if on the page
      )}
    >
      <div
        className={classNames("w-6 h-6 flex justify-center items-center", {
          "mr-2": !minimized,
        })}
      >
        <Tooltip label={label}>
          <div
            className={classNames(
              "md:text-gray-500",
              { "text-gray-500": props.minimized },
              { "text-white": !props.minimized && !isOnPage },
              { "text-gray-500": isOnPage },
            )}
          >
            {icon}
          </div>
        </Tooltip>
      </div>
      {!minimized && label}
      {!!submenu && !minimized && submenu.length > 0 && (
        <div
          className={classNames(
            "ml-auto mr-2 transition-transform duration-300",
            { "rotate-180 h-fit": isOpen },
          )}
        >
          <ChevronDown size={16} />
        </div>
      )}
    </div>
  );

  if (submenu && submenu.length > 0) {
    return (
      <div>
        <button className="w-full" onClick={() => setIsOpen(!isOpen)}>
          {content}
        </button>
        <div
          className={classNames("overflow-hidden transition-all duration-400", {
            "max-h-0": !isOpen,
            "max-h-screen": isOpen,
          })}
        >
          {submenu.map((item) => (
            <item.component
              key={item.key}
              minimized={minimized}
              setMobileMenuOpen={setMobileMenuOpen}
            />
          ))}
        </div>
      </div>
    );
  }

  if (onClick) {
    return <button onClick={onClick}>{content}</button>;
  }

  return (
    <NavLink
      to={to as string}
      className="nav-menu-link group/menuitem outline-none ring-0 hover:cursor-pointer"
      onClick={() => setMobileMenuOpen(false)}
    >
      {content}
    </NavLink>
  );
}
